import { Upload, Zap, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AnalyserReport from "../../components/AdminPortal/AnalyserReports/AnalyserReport";
import TopNavbar from "../../components/AdminPortal/TopNavbar";
import { useSidebar } from "../../context/SidebarContext";
import { analyzerAPI } from "../../services/AdminPortal/analyzerAPI";
import { crmAPI } from "../../services/AdminPortal/crmAPI";

const UniversalAnalysers = () => {
  const { sidebarExpanded, isMobile } = useSidebar();
  const { id } = useParams();
  const navigate = useNavigate();

  const [analyzerType, setAnalyzerType] = useState("");
  const [submittedDocuments, setSubmittedDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedSubmittedDocument, setSelectedSubmittedDocument] = useState([]);

  const fileInputRef = useRef(null);

  const analyzerTypes = [
    { value: "business-profile", label: "Business Analyzer" },
    { value: "bank-statement", label: "Bank Statement Analyzer" },
    { value: "credit-report", label: "Credit Analyzer" },
    { value: "payslip", label: "Employment Payslip Analyzer" },
    { value: "itr", label: "ITR Analyzer" },
    { value: "loan-statement", label: "Loan Statement Analyzer" },
    { value: "rental-agreement", label: "Rental Agreement Analyzer" },
    { value: "business-license", label: "Business License Analyzer" },
    { value: "property-document", label: "Property Document Analyzer" },
    { value: "evaluation-board", label: "Evaluation Analyzer" },
  ];

  const layoutStyles = {
    marginLeft: isMobile ? "0" : sidebarExpanded ? "200px" : "60px",
    width: isMobile
      ? "100%"
      : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
  };

  useEffect(() => {
    fetchSubmittedDocuments();
  }, []);

  const fetchSubmittedDocuments = async () => {
    try {
      const documents = await crmAPI.getLeadDocuments(id);
      setSubmittedDocuments(documents);
    } catch (error) {
      console.error("Error fetching submitted documents:", error);
      toast.error("Failed to load submitted documents");
    }
  };

  const handleAnalyzerTypeChange = (selectedType) => {
    setAnalyzerType(selectedType);
    setAnalysisResult(null);
    setSelectedSubmittedDocument([]);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
    }
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    if (newFiles.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // ✅ Run analyser with blue loading indicator
  const handleRunAnalyser = async () => {
    if (!analyzerType) {
      toast.error("Please select analyzer type");
      return;
    }

    if (selectedFiles.length === 0 && selectedSubmittedDocument.length === 0) {
      toast.error("Please either upload files or select submitted documents");
      return;
    }

    setLoading(true);
    try {
      let response;

      if (selectedFiles.length > 0) {
        response = await analyzerAPI.analyzeMultiFormatDocuments(
          analyzerType,
          selectedFiles,
          id,
          null
        );
      } else {
        response = await analyzerAPI.analyzePdfUrlDocuments(
          analyzerType,
          selectedSubmittedDocument,
          id,
          null
        );
      }

      if (response.success) {
        toast.success("Analysis completed successfully!");
        setAnalysisResult({
          status: "completed",
          message: response.message,
          results: response.results,
          analyzerType,
          _id: response.reportId,
        });
      } else {
        throw new Error(response.error || "Analysis failed");
      }

      // Clear selections
      setSelectedFiles([]);
      setSelectedSubmittedDocument([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error running analyzer:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response,
        data: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.error || error.message || "Failed to run analyzer";
      toast.error(errorMessage);
      setAnalysisResult({
        status: "failed",
        errorMessage: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-black text-white mt-12"
      style={layoutStyles}
    >
      <TopNavbar />
      <div className="p-4">
        {/* Breadcrumbs */}
        <div className="mb-6 flex flex-wrap items-center justify-start gap-4">
          <span
            className="text-[#FFCA20] cursor-pointer underline"
            onClick={() => navigate("/admin/customers")}
          >
            Loan Applications
          </span>
          <span className="text-white"> &gt; </span>
          <span
            className="text-[#FFCA20] cursor-pointer underline"
            onClick={() => navigate(`/admin/loan-applications/approval/${id}`)}
          >
            Application Review
          </span>
          <span className="text-white"> &gt; </span>
          <span className="text-white">Universal Analysers</span>
        </div>

        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 bg-[#131313] px-6 py-4 rounded-md">
          <div>
            <h1 className="text-white text-xl font-semibold mb-1">
              Universal Analysers
            </h1>
            <p className="text-gray-400 text-sm">
              Select an analyzer below to view detailed insights
            </p>
          </div>
        </div>

        {/* Input Fields Section */}
        <div className="space-y-6 bg-[#131313] p-6 rounded-lg border border-[#333]">
          {/* Analyzer Type */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Analyzer Type
            </label>
            <select
              value={analyzerType}
              onChange={(e) => handleAnalyzerTypeChange(e.target.value)}
              className="w-full bg-[#1f1f1f] border border-[#444] text-white rounded-md px-4 py-3 focus:outline-none focus:border-[#FFCA20] transition-colors"
            >
              <option value="">Select analyzer type</option>
              {analyzerTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Submitted Documents */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Submitted Documents (Select Multiple)
            </label>
            {submittedDocuments.length > 0 ? (
              <div className="bg-[#1f1f1f] border border-[#444] rounded-md p-3 max-h-40 overflow-y-auto">
                {submittedDocuments.map((doc) => (
                  <label
                    key={doc._id}
                    className="flex items-center space-x-2 mb-2 cursor-pointer hover:bg-[#2a2a2a] p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      value={doc._id}
                      checked={selectedSubmittedDocument.includes(doc._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedSubmittedDocument((prev) => [
                            ...prev,
                            doc._id,
                          ]);
                        } else {
                          setSelectedSubmittedDocument((prev) =>
                            prev.filter((id) => id !== doc._id)
                          );
                        }
                      }}
                      className="appearance-none h-5 w-5 border-2 border-white/20 rounded-sm bg-transparent checked:transparent checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                    before:content-['✓'] before:absolute before:text-[#ffca20] before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                    />
                    <span className="text-white text-sm">
                      {doc.documentName}
                    </span>
                    <span className="text-gray-400 text-xs">
                      ({doc.documentType})
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="bg-[#1f1f1f] border border-[#444] text-gray-400 rounded-md px-4 py-3 text-sm">
                No documents submitted
              </div>
            )}
          </div>

          {/* Upload Section */}
          <div className="bg-[#131313] p-4 rounded-lg border border-[#333]">
            <h4 className="text-[#FFCA20] text-sm font-medium mb-3">
              Upload Document for Analysis
            </h4>

            {/* Upload Box */}
            <div
              className="w-1/2 mx-auto flex flex-col items-center justify-center p-6 border border-[#333] rounded-md bg-[#131313] cursor-pointer hover:border-[#FFCA20] transition"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-8 h-8 text-[#FFCA20] mb-2" />
              <p className="text-white text-sm font-medium">
                Click to browse files
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Supported formats: PDF, JPG, PNG (Max 10 MB each, up to 10 files)
              </p>

              {/* Selected files list */}
              {selectedFiles.length > 0 && (
                <div className="mt-4 w-full space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between w-full text-sm text-gray-300 bg-[#1f1f1f] px-3 py-2 rounded-md"
                    >
                      <span className="truncate">{file.name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                        className="ml-4 text-red-500 hover:text-red-600 text-lg leading-none"
                        title="Remove file"
                      >
                        ✖
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                className="hidden"
              />
            </div>
          </div>

          {/* Start Automation Button */}
          {analyzerType && (
            <div className="text-center pt-2">
              <button
                onClick={handleRunAnalyser}
                disabled={
                  loading ||
                  (selectedFiles.length === 0 &&
                    selectedSubmittedDocument.length === 0)
                }
                className={`px-6 py-2 rounded-sm flex items-center gap-2 text-sm font-medium mx-auto
          ${
            loading ||
            (selectedFiles.length === 0 &&
              selectedSubmittedDocument.length === 0)
              ? "bg-gray-600 text-gray-400 cursor-not-allowed"
              : "bg-[#FFCA20] hover:bg-[#e6b91a] text-black"
          }`}
              >
                {loading ? "Processing..." : "Start Automation"}
                <Zap className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div className="mt-6 text-center">
            <Loader2 className="animate-spin text-blue-400 w-8 h-8 mx-auto mb-3" />
            <p className="text-blue-400 font-medium">
              Please wait ~30 sec for the document to be analysed...
            </p>
          </div>
        )}

        {/* Results Section */}
        {analysisResult && !loading && (
          <div className="mt-8 p-6 bg-[#131313] rounded-lg border border-[#333333]">
            <h3 className="text-xl font-semibold text-white mb-4">
              Analysis Results
            </h3>

            {analysisResult.status === "completed" && (
              <div>
                <div className="text-center mb-6">
                  <p className="text-green-400 mb-4">
                    Analysis completed successfully!
                  </p>
                </div>

                <AnalyserReport
                  analyzerType={analysisResult.analyzerType}
                  analysisResult={analysisResult}
                />
              </div>
            )}

            {analysisResult.status === "failed" && (
              <div className="text-center">
                <h4 className="text-lg font-medium text-red-400 mb-2">
                  Analysis Failed
                </h4>
                <p className="text-gray-400 mb-4">
                  {analysisResult.errorMessage ||
                    "An error occurred during analysis"}
                </p>
                <button
                  onClick={handleRunAnalyser}
                  className="bg-[#FFCA20] text-black px-4 py-2 rounded-lg hover:bg-[#e6b91a] transition-colors"
                >
                  Retry Analysis
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default UniversalAnalysers;